/**
 * Cart Bundle Nesting
 *
 * Line items sharing the same `properties._bundle_id` (set by
 * bundle-multi-color.js when a multi-quantity tier is added) are rendered
 * as one nested group (see cart-drawer-bundle-group.liquid /
 * cart-line-item-bundle-group.liquid).
 *
 * Two actions, both handled here directly via the raw Cart AJAX API, each
 * in a SINGLE network request:
 * - Trash button (`.cart-bundle-group__remove`) removes every line in the
 *   set at once.
 * - `+`/`-` stepper (`data-bundle-qty-increase` / `-decrease`) changes every
 *   line's quantity together, scaled by how many units of that colour make
 *   up one set (`data-unit-qty`, from bundle-multi-color.js's
 *   `_bundle_unit_qty` property), so the ratio between colours never
 *   drifts even when a colour was picked in more than one slot.
 *
 * Both read each line's current key/quantity/unit-qty straight off the
 * already-rendered `.cart-bundle-group__child` elements instead of
 * fetching /cart.js first — that DOM only ever reflects this same script's
 * own last section-HTML swap, so it's already current. Combined with
 * asking /cart/update.js to also render the section HTML in the same
 * response (`sections`/`sections_url` params — standard Shopify Ajax Cart
 * API), this whole flow is one round trip instead of three (fetch cart,
 * update, re-fetch section HTML separately).
 *
 * Still exactly one mutation per action (not a sequence of them) — this
 * matters because a line's `key` (variant_id:properties_hash) isn't stable
 * across SEPARATE mutations if the product has an automatic discount tied
 * to quantity — sequential single-line changes (old approach) could act on
 * a stale key or an unevenly-updated set. Buttons are disabled
 * synchronously before the request starts to rule out double-clicks.
 *
 * None of these use the theme's own cart-item remove/qty-button classes on
 * purpose — the theme's native delegated handlers act on a single line by
 * positional index into their own cached cart, so matching those classes
 * would race this handler instead of cooperating with it.
 */
(function () {
  'use strict';

  if (window.__cartBundleNestingBound) return;
  window.__cartBundleNestingBound = true;

  var updateUrl = (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart_update_url) || '/cart/update.js';

  function sectionIdFor(itemEl) {
    return itemEl.closest('#cart-drawer-container') ? 'cart-drawer' : 'cart-template';
  }

  function domBundleLines(bundleId) {
    var lines = [];
    document.querySelectorAll('.cart-bundle-group__child[data-bundle-id="' + bundleId + '"]').forEach(function (el) {
      lines.push({
        key: el.getAttribute('data-id'),
        variantId: el.getAttribute('data-variant-id'),
        slot: el.getAttribute('data-slot') || '',
        quantity: parseInt(el.getAttribute('data-quantity'), 10) || 0,
        unitQty: parseInt(el.getAttribute('data-unit-qty'), 10) || 1,
      });
    });
    return lines;
  }

  function updateLines(updates, sectionId) {
    var body = { updates: updates };
    if (sectionId) {
      body.sections = sectionId;
      body.sections_url = window.location.pathname;
    }
    return fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) { return res.json(); });
  }

  // Re-adds a bundle's slots as fresh cart lines via /cart/add.js instead of
  // changing quantities on existing lines via /cart/update.js. An automatic
  // quantity discount (e.g. "buy 3, 1 free") can split a slot's units across
  // two lines sharing the same variant_id — a /cart/update.js response that
  // shows that split appears to confuse this store's Orbe geolocation
  // app-embed into force-reloading the page (its own bug, reproduced and
  // reported, but out of our control). The exact same discount-driven split
  // happens after a plain /cart/add.js call too, and that never triggers
  // the reload — so this store's Orbe script appears to key off the
  // /cart/update.js endpoint specifically, not the resulting cart shape.
  function addItems(items, sectionId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionId, sections_url: window.location.pathname }),
    }).then(function (res) { return res.json(); });
  }

  function applySectionHTML(data, itemEl, sectionId) {
    var html = data.sections && data.sections[sectionId];
    if (!html) return;

    var wrapper = itemEl.closest('.scd__wrapper') || document;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newBody = doc.querySelector('.scd__body');
    var newSummary = doc.querySelector('.scd__summary');
    var oldBody = wrapper.querySelector('.scd__body');
    var oldSummary = wrapper.querySelector('.scd__summary');
    if (newBody && oldBody) oldBody.replaceWith(newBody);
    if (newSummary && oldSummary) oldSummary.replaceWith(newSummary);

    document.querySelectorAll('.sf-cart-count').forEach(function (el) {
      el.textContent = data.item_count;
    });

    // Emit so other theme/app logic (e.g. a "free gift above $X" app watching
    // for cart changes) can react — the DOM is already up to date via the
    // replaceWith() calls above regardless of what this triggers elsewhere.
    // (Earlier suspected of causing the Orbe reload; later diagnosis showed
    // the real cause was calling /cart/update.js at all for quantity
    // increases, now fixed by routing increases through /cart/add.js
    // instead — so this is safe to emit again.)
    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('ON_CART_UPDATE', data);
    }
  }

  function handleError(err) {
    console.error('[cart-bundle-nesting] Cart update failed:', err);
  }

  // ─── Remove whole set ──────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest && e.target.closest('.cart-bundle-group__remove');
    if (!removeBtn) return;

    var itemEl = removeBtn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = removeBtn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || removeBtn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    removeBtn.disabled = true;

    var sectionId = sectionIdFor(itemEl);
    var updates = {};
    domBundleLines(bundleId).forEach(function (line) {
      updates[line.key] = 0;
    });

    updateLines(updates, sectionId)
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(handleError)
      .finally(function () {
        removeBtn.disabled = false;
      });
  }, true);

  // ─── Set-count stepper (+/-) ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bundle-qty-increase], [data-bundle-qty-decrease]');
    if (!btn) return;

    var itemEl = btn.closest('[data-bundle-id]');
    if (!itemEl) return;

    var bundleId = btn.getAttribute('data-bundle-id') || itemEl.getAttribute('data-bundle-id');
    if (!bundleId || btn.disabled) return;

    var delta = btn.hasAttribute('data-bundle-qty-increase') ? 1 : -1;

    e.preventDefault();
    e.stopImmediatePropagation();

    var groupButtons = document.querySelectorAll(
      '[data-bundle-id="' + bundleId + '"][data-bundle-qty-increase], [data-bundle-id="' + bundleId + '"][data-bundle-qty-decrease]'
    );
    groupButtons.forEach(function (el) { el.disabled = true; });

    var sectionId = sectionIdFor(itemEl);
    var rawLines = domBundleLines(bundleId);

    // Group every existing line by slot so we know, per colour: which
    // variant to touch, how many units make up one set, and the current
    // total across however many lines that slot is currently split into.
    var slots = {};
    rawLines.forEach(function (line) {
      var s = slots[line.slot] || (slots[line.slot] = { variantId: line.variantId, unitQty: line.unitQty, total: 0, keys: [] });
      s.total += line.quantity;
      s.keys.push(line.key);
    });

    var request;

    if (delta > 0) {
      // Increase: add the extra unit(s) for each slot via /cart/add.js —
      // same variant_id + properties as the existing line, so Shopify
      // merges it in rather than creating a visibly separate line. This
      // never touches /cart/update.js at all, which is both the fastest
      // path (one request) and avoids the one thing we've confirmed
      // triggers this store's Orbe geolocation app-embed into force-
      // reloading the page: a /cart/update.js response that shows a
      // discount-driven split (two lines sharing one variant_id). The
      // exact same split coming from a /cart/add.js response never
      // triggers it.
      var addItemsPayload = Object.keys(slots).map(function (slot) {
        var s = slots[slot];
        return {
          id: s.variantId,
          quantity: s.unitQty * delta,
          properties: { _bundle_id: bundleId, _bundle_slot: slot, _bundle_unit_qty: String(s.unitQty) },
        };
      });
      request = addItems(addItemsPayload, sectionId);
    } else {
      // Decrease: /cart/add.js can't subtract, so this has to go through
      // /cart/update.js — but as a single call, and collapsing each slot
      // onto just its first line (every other line for that slot goes to
      // 0) so we never leave stale extra lines behind across repeated
      // clicks, and never leave the cart in a temporary in-between empty
      // state the way a separate zero-out step would.
      var updates = {};
      Object.keys(slots).forEach(function (slot) {
        var s = slots[slot];
        var newTotal = Math.max(s.unitQty, s.total + (delta * s.unitQty));
        s.keys.forEach(function (key, idx) {
          updates[key] = idx === 0 ? newTotal : 0;
        });
      });
      request = updateLines(updates, sectionId);
    }

    request
      .then(function (data) {
        applySectionHTML(data, itemEl, sectionId);
      })
      .catch(function (err) {
        handleError(err);
        groupButtons.forEach(function (el) { el.disabled = false; });
      });
  }, true);
})();
